import { Button, Card, DatePicker, Form, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type RouteRow = { id: string; code: string; name: string };

type BoardingRow = {
  id: string;
  route_id: string;
  route_code?: string;
  route_name?: string;
  run_date: string;
  direction: string;
  status: string;
  vehicle_label?: string | null;
  marked_count?: number;
  boarded_count?: number;
  absent_count?: number;
};

export default function TransportBoardingPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const routeId = params.get('route_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const routes = useQuery({
    queryKey: ['education-transport-routes'],
    queryFn: async () => {
      const res = await api.get<RouteRow[]>('/education/transport/routes');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const runs = useQuery({
    queryKey: ['education-transport-boarding', routeId],
    queryFn: async () => {
      const res = await api.get<BoardingRow[]>('/education/transport/boarding', {
        params: { route_id: routeId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const createRun = useMutation({
    mutationFn: (values: { run_date: string; direction: string }) =>
      api.post('/education/transport/boarding', {
        route_id: routeId,
        run_date: values.run_date,
        direction: values.direction,
        seed_roster: true,
      }),
    onSuccess: (result) => {
      const body = result.data as { id?: string; seeded?: number };
      message.success(t('transportBoardingCreated', { seeded: body?.seeded ?? 0 }));
      queryClient.invalidateQueries({ queryKey: ['education-transport-boarding'] });
      if (body?.id) navigate(`/education/transport/boarding/${body.id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('transportBoarding')}
      description={t('transportBoardingIntro')}
      extra={<Button onClick={() => navigate('/education/transport')}>{t('backToTransport')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('transportRoute')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 260 }}
                value={routeId}
                placeholder={t('selectTransportRoute')}
                options={(routes.data || []).map((r) => ({
                  value: r.id,
                  label: `${r.code} — ${r.name}`,
                }))}
                onChange={(value) => {
                  const next = new URLSearchParams(params);
                  if (value) next.set('route_id', value);
                  else next.delete('route_id');
                  setParams(next);
                }}
              />
            </Form.Item>
          </Form>
        </Card>

        {canManage && routeId ? (
          <Card size="small" title={t('newBoardingRun')}>
            <Form
              layout="inline"
              initialValues={{ direction: 'morning', run_date: dayjs() }}
              onFinish={(values: { run_date: dayjs.Dayjs; direction: string }) =>
                createRun.mutate({
                  run_date: values.run_date.format('YYYY-MM-DD'),
                  direction: values.direction,
                })
              }
            >
              <Form.Item name="run_date" label={t('runDate')} rules={[{ required: true }]}>
                <DatePicker />
              </Form.Item>
              <Form.Item name="direction" label={t('direction')} rules={[{ required: true }]}>
                <Select
                  style={{ width: 140 }}
                  options={[
                    { value: 'morning', label: t('directionMorning') },
                    { value: 'afternoon', label: t('directionAfternoon') },
                    { value: 'other', label: t('directionOther') },
                  ]}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={createRun.isPending}>
                {t('createBoardingWithRoster')}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={runs.isLoading}
            dataSource={runs.data || []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('transportRoute'),
                render: (_: unknown, row: BoardingRow) =>
                  `${row.route_code || ''} — ${row.route_name || ''}`,
              },
              { title: t('runDate'), dataIndex: 'run_date', width: 120 },
              {
                title: t('direction'),
                dataIndex: 'direction',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('boardingSummary'),
                width: 150,
                render: (_: unknown, row: BoardingRow) =>
                  `${row.boarded_count ?? 0}B / ${row.absent_count ?? 0}A / ${row.marked_count ?? 0}`,
              },
              {
                title: t('actions'),
                width: 100,
                render: (_: unknown, row: BoardingRow) => (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => navigate(`/education/transport/boarding/${row.id}`)}
                  >
                    {t('open')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
